import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Pause, Square, Volume2, Upload, Download, Settings } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface Voice {
  voice: SpeechSynthesisVoice;
  name: string;
  lang: string;
  localService: boolean;
}

export const TextToSpeechTool: React.FC = () => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [voices, setVoices] = useState<Voice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>('');
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [volume, setVolume] = useState(1);
  const [currentUtterance, setCurrentUtterance] = useState<SpeechSynthesisUtterance | null>(null);

  // Load available voices
  useEffect(() => {
    const loadVoices = () => {
      const availableVoices = speechSynthesis.getVoices();
      const voiceList: Voice[] = availableVoices.map(voice => ({
        voice,
        name: voice.name,
        lang: voice.lang,
        localService: voice.localService
      }));
      
      setVoices(voiceList);
      
      // Set default voice (prefer English voices)
      const defaultVoice = voiceList.find(v => v.lang.startsWith('en')) || voiceList[0];
      if (defaultVoice && !selectedVoice) {
        setSelectedVoice(defaultVoice.voice.name);
      }
    };

    loadVoices();
    
    // Some browsers load voices asynchronously
    if (speechSynthesis.onvoiceschanged !== undefined) {
      speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, [selectedVoice]);

  const speak = () => {
    if (!text.trim()) {
      toast.error(t('common.enterTextToSpeak'));
      return;
    }

    // Stop any current speech
    speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    
    // Find selected voice
    const voice = voices.find(v => v.voice.name === selectedVoice)?.voice;
    if (voice) {
      utterance.voice = voice;
    }
    
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.volume = volume;

    utterance.onstart = () => {
      setIsPlaying(true);
      setIsPaused(false);
    };

    utterance.onend = () => {
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentUtterance(null);
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      toast.error(t('common.speechSynthesisFailed'));
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentUtterance(null);
    };

    setCurrentUtterance(utterance);
    speechSynthesis.speak(utterance);
  };

  const pause = () => {
    if (speechSynthesis.speaking && !speechSynthesis.paused) {
      speechSynthesis.pause();
      setIsPaused(true);
    }
  };

  const resume = () => {
    if (speechSynthesis.paused) {
      speechSynthesis.resume();
      setIsPaused(false);
    }
  };

  const stop = () => {
    speechSynthesis.cancel();
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentUtterance(null);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setText(content);
      };
      reader.readAsText(file);
    }
  };

  const downloadAudio = async () => {
    if (!text.trim()) {
      toast.error(t('common.enterTextFirst'));
      return;
    }

    try {
      // Note: This is a simplified approach. In a real application,
      // you might want to use Web Audio API or a server-side solution
      // for better audio quality and format options.
      toast.info(t('common.audioDownloadInfo'));
    } catch (err) {
      toast.error(t('common.failedToGenerateAudio'));
    }
  };

  const clearText = () => {
    setText('');
    stop();
  };

  const sampleTexts = [
    t('tools.textToSpeech.sampleTexts.sample1'),
    t('tools.textToSpeech.sampleTexts.sample2'),
    t('tools.textToSpeech.sampleTexts.sample3'),
    t('tools.textToSpeech.sampleTexts.sample4')
  ];

  const useSample = (sample: string) => {
    setText(sample);
    stop();
  };

  // Group voices by language
  const groupedVoices = voices.reduce((groups, voice) => {
    const lang = voice.lang.split('-')[0];
    if (!groups[lang]) {
      groups[lang] = [];
    }
    groups[lang].push(voice);
    return groups;
  }, {} as Record<string, Voice[]>);

  const languageNames: Record<string, string> = {
    'en': t('tools.textToSpeech.languages.english'),
    'es': t('tools.textToSpeech.languages.spanish'),
    'fr': t('tools.textToSpeech.languages.french'),
    'de': t('tools.textToSpeech.languages.german'),
    'it': t('tools.textToSpeech.languages.italian'),
    'pt': t('tools.textToSpeech.languages.portuguese'),
    'ru': t('tools.textToSpeech.languages.russian'),
    'ja': t('tools.textToSpeech.languages.japanese'),
    'ko': t('tools.textToSpeech.languages.korean'),
    'zh': t('tools.textToSpeech.languages.chinese'),
    'ar': t('tools.textToSpeech.languages.arabic'),
    'hi': t('tools.textToSpeech.languages.hindi')
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Volume2 className="w-5 h-5" />
            <span>{t('tools.textToSpeech.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Voice Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.textToSpeech.voice')}:
              </label>
              <select
                value={selectedVoice}
                onChange={(e) => setSelectedVoice(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                {Object.entries(groupedVoices).map(([lang, voiceList]) => (
                  <optgroup key={lang} label={languageNames[lang] || lang.toUpperCase()}>
                    {voiceList.map((voice) => (
                      <option key={voice.voice.name} value={voice.voice.name}>
                        {voice.name} {voice.localService ? t('tools.textToSpeech.local') : t('tools.textToSpeech.remote')}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {/* Rate Control */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.textToSpeech.speed')}: {rate}x
              </label>
              <input
                type="range"
                min="0.1"
                max="2"
                step="0.1"
                value={rate}
                onChange={(e) => setRate(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>

            {/* Pitch Control */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.textToSpeech.pitch')}: {pitch}
              </label>
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={pitch}
                onChange={(e) => setPitch(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>

            {/* Volume Control */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.textToSpeech.volume')}: {Math.round(volume * 100)}%
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              onClick={isPlaying && !isPaused ? pause : isPaused ? resume : speak}
              variant="primary"
              disabled={!text.trim()}
            >
              {isPlaying && !isPaused ? (
                <>
                  <Pause className="w-4 h-4 mr-2" />
                  {t('tools.textToSpeech.pause')}
                </>
              ) : isPaused ? (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  {t('tools.textToSpeech.resume')}
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  {t('tools.textToSpeech.speak')}
                </>
              )}
            </Button>
            <Button
              onClick={stop}
              disabled={!isPlaying && !isPaused}
              variant="outline"
            >
              <Square className="w-4 h-4 mr-2" />
              {t('tools.textToSpeech.stop')}
            </Button>
            <Button
              onClick={downloadAudio}
              disabled={!text.trim()}
              variant="outline"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('tools.textToSpeech.downloadAudio')}
            </Button>
            <Button
              onClick={clearText}
              variant="outline"
            >
              {t('common.clear')}
            </Button>
          </div>

          <div className="mb-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.md"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="mb-2"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('common.selectFile')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Text Input */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t('common.textToSpeak')}</span>
            {text && (
              <span className="text-sm text-gray-500">
                {text.split(' ').filter(word => word.length > 0).length} {t('common.words')}, {text.length} {t('common.characters')}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('common.enterTextToConvert')}
            className="min-h-[200px]"
          />

          {/* Sample Texts */}
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('tools.textToSpeech.sampleTexts.title')}:
            </h4>
            <div className="space-y-2">
              {sampleTexts.map((sample, index) => (
                <Button
                  key={index}
                  onClick={() => useSample(sample)}
                  variant="outline"
                  size="sm"
                  className="text-left h-auto p-2 whitespace-normal"
                >
                  {sample}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status and Info */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.textToSpeech.speechStatus')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                {isPlaying ? (isPaused ? t('tools.textToSpeech.paused') : t('tools.textToSpeech.speaking')) : t('tools.textToSpeech.ready')}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.textToSpeech.status')}</div>
            </div>
            <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="text-lg font-bold text-green-600 dark:text-green-400">
                {voices.length}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.textToSpeech.availableVoices')}</div>
            </div>
            <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                {text ? Math.ceil(text.split(' ').filter(word => word.length > 0).length / 150) : 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.textToSpeech.estMinutes')}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Browser Compatibility Note */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3 text-sm text-amber-700 dark:text-amber-400">
            <Settings className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">{t('tools.textToSpeech.browserCompatibility')}:</p>
              <p>{t('tools.textToSpeech.compatibilityNote')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
